import React, { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import "./SignPdfFinishModal.css";

/**
 * Terminal step — Download, Print, or Share the signed PDF.
 */
const SignPdfFinishModal = ({
  open = false,
  onClose = () => {},
  onDownload = () => {},
  onDownloadLater = () => {},
  onPrint = () => {},
  onEmailPdf = () => {},
  onShare = () => {},
  busy = false,
  shareSupported = false,
}) => {
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  const node = (
    <div className="sp-finish-root" role="presentation">
      <button
        type="button"
        className="sp-finish-backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className="sp-finish-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button
          type="button"
          className="sp-finish-close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>
        <h2 id={titleId} className="sp-finish-title">
          Your document is ready
        </h2>
        <p className="sp-finish-sub">
          Download a signed copy, print it, email it, or share when your browser
          supports it.
        </p>
        <div className="sp-finish-actions">
          <button
            type="button"
            className="sp-finish-action sp-finish-action--primary"
            disabled={busy}
            onClick={onDownload}
          >
            <span className="sp-finish-action__icon" aria-hidden="true">
              ↓
            </span>
            <span className="sp-finish-action__copy">
              <span className="sp-finish-action__label">Download PDF</span>
              <span className="sp-finish-action__hint">
                Save the signed document to your device
              </span>
            </span>
          </button>
          <button
            type="button"
            className="sp-finish-action"
            disabled={busy}
            onClick={onDownloadLater}
          >
            <span className="sp-finish-action__icon" aria-hidden="true">
              ⏱
            </span>
            <span className="sp-finish-action__copy">
              <span className="sp-finish-action__label">Download later</span>
              <span className="sp-finish-action__hint">
                Come back and download when you are ready
              </span>
            </span>
          </button>
          <button
            type="button"
            className="sp-finish-action"
            disabled={busy}
            onClick={onPrint}
          >
            <span className="sp-finish-action__icon" aria-hidden="true">
              🖨
            </span>
            <span className="sp-finish-action__copy">
              <span className="sp-finish-action__label">Print</span>
              <span className="sp-finish-action__hint">
                Open the print dialog for this PDF
              </span>
            </span>
          </button>
          <button
            type="button"
            className="sp-finish-action"
            disabled={busy}
            onClick={onEmailPdf}
          >
            <span className="sp-finish-action__icon" aria-hidden="true">
              ✉
            </span>
            <span className="sp-finish-action__copy">
              <span className="sp-finish-action__label">Email PDF</span>
              <span className="sp-finish-action__hint">
                Open your email app to send the signed PDF
              </span>
            </span>
          </button>
          {shareSupported ? (
            <button
              type="button"
              className="sp-finish-action"
              disabled={busy}
              onClick={onShare}
            >
              <span className="sp-finish-action__icon" aria-hidden="true">
                ↗
              </span>
              <span className="sp-finish-action__copy">
                <span className="sp-finish-action__label">Share</span>
                <span className="sp-finish-action__hint">
                  Send via your device share sheet
                </span>
              </span>
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default SignPdfFinishModal;
